// Stripe checkout helpers. The subscription plans are driven entirely by the
// backend (GET /api/plans, derived from STRIPE_ALLOWED_PRICE_IDS), so the frontend
// never hardcodes a price ID — the same code works in test and live automatically.
import { authHeaders } from './authToken';

const CURRENCY_SYMBOL = { EUR: '€', GBP: '£', USD: '$' };

/** Fetch the offered plans. Returns [] on any error or when Stripe isn't set up. */
export async function fetchPlans() {
  try {
    const res = await fetch('/api/plans', { headers: { ...authHeaders() } });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.plans) ? data.plans : [];
  } catch {
    return [];
  }
}

/** A plan's price as a short label, e.g. "€4.99 / month" or "€39 / year". */
export function formatPlan(plan) {
  const amount = (plan.amount ?? 0) / 100;
  const cur    = (plan.currency || 'eur').toUpperCase();
  const sym    = CURRENCY_SYMBOL[cur];
  // Drop trailing .00 (e.g. €39, not €39.00) but keep real cents (€4.99).
  const num    = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const price  = sym ? `${sym}${num}` : `${num} ${cur}`;
  const per    = plan.interval === 'year' ? 'year' : plan.interval === 'month' ? 'month' : null;
  return per ? `${price} / ${per}` : price;
}

/**
 * Start Stripe Checkout for a price and redirect the browser to it. Requires an
 * authenticated user (the backend ties the session to g.user_id). On success the
 * page navigates away; on failure returns {ok:false, error} for the caller to show.
 */
export async function startCheckout(priceId) {
  try {
    const res = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ price_id: priceId }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.checkout_url) {
      window.location.href = data.checkout_url;
      return { ok: true };
    }
    return { ok: false, error: data.error || 'Could not start checkout. Please try again.' };
  } catch {
    return { ok: false, error: 'Could not reach the server. Please try again.' };
  }
}
